import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import {Alert, FormControlLabel, Stack, Switch, Typography} from "@mui/material";
import RuleIcon from '@mui/icons-material/Rule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PageTitle from "./layout/PageTitle";
import Loader from "./layout/loading/loader";
import {anomaliesActions} from "../../modules/anomalies/anomalies.actions.js";
import {problemsActions} from "../../modules/problems/problems.actions";
import AnomaliesTable from "./controleQualite/AnomaliesTable.js";
import AnomalieDetailDrawer from "./controleQualite/AnomalieDetailDrawer.js";
import {countRecidives, filterRecidives} from "./controleQualite/recidiveRules.js";

/**
 * Contrôle Qualité — file de triage des anomalies de saisie terrain.
 *
 * Deux types d'anomalies dans une même liste :
 * - ECART_QUANTITE : nombre lavé ≠ quantité du conteneur au patrimoine.
 * - LAVAGE_IMPOSSIBLE : conteneur inaccessible, à vérifier/valider (raison + photos).
 * Pour chaque ligne, l'exploitant tranche photos à l'appui.
 * Un lavage impossible peut porter le tag RÉCIDIVE (CQ-01 : passage précédent déjà impossible,
 * calculé par l'API) — filtre « récidives seules » pour les traiter en priorité.
 */
function ControleQualitePage({dispatch, anomalies, problems, loading, resolving, error}) {
    const [selectedAnomalie, setSelectedAnomalie] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [recidivesSeules, setRecidivesSeules] = useState(false);

    useEffect(() => {
        dispatch(anomaliesActions.fetchAnomalies());
        dispatch(problemsActions.fetchProblems()); // options du sélecteur de problèmes (drawer)
    }, [dispatch]);

    const handleRowClick = (anomalie) => {
        setSelectedAnomalie(anomalie);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedAnomalie(null);
    };

    const handlePatrimoineMaj = (anomalie) => {
        dispatch(anomaliesActions.appliquerAuPatrimoine(anomalie));
        closeDrawer();
    };

    const handleCorriger = (anomalie, values) => {
        dispatch(anomaliesActions.corrigerIntervention(anomalie, values));
        closeDrawer();
    };

    const handleIgnorer = (anomalie) => {
        dispatch(anomaliesActions.ignorerAnomalie(anomalie));
        closeDrawer();
    };

    const handleAcquitter = (anomalie) => {
        dispatch(anomaliesActions.acquitterLavageImpossible(anomalie));
        closeDrawer();
    };

    const handleModifierProbleme = (anomalie, problemeIris) => {
        dispatch(anomaliesActions.modifierProbleme(anomalie, problemeIris));
        closeDrawer();
    };

    const isEmpty = !loading && (anomalies?.length ?? 0) === 0;
    const nbRecidives = countRecidives(anomalies);
    const anomaliesAffichees = filterRecidives(anomalies, recidivesSeules);

    return (
        <>
            <PageTitle title={"Contrôle Qualité"} icon={<RuleIcon/>}/>

            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Écarts de saisie et lavages impossibles à vérifier.
                À traiter avant l'édition des bilans.
            </Typography>

            {error &&
                <Alert severity="error" sx={{mb: 2}}>{error}</Alert>
            }

            {loading &&
                <Loader/>
            }

            {isEmpty && !error &&
                <Stack alignItems="center" spacing={1} sx={{py: 8}}>
                    <CheckCircleOutlineIcon sx={{fontSize: 64, color: '#7bb421'}}/>
                    <Typography variant="h6">Tout est en ordre</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Aucune anomalie à traiter. Les bilans peuvent partir sereinement.
                    </Typography>
                </Stack>
            }

            {!loading && !isEmpty &&
                <>
                    <Stack direction="row" justifyContent="flex-end" sx={{mb: 1}}>
                        <FormControlLabel
                            control={<Switch size="small" checked={recidivesSeules} disabled={nbRecidives === 0}
                                             onChange={(e) => setRecidivesSeules(e.target.checked)}/>}
                            label={`Récidives seules (${nbRecidives})`}
                        />
                    </Stack>
                    <AnomaliesTable anomalies={anomaliesAffichees} onRowClick={handleRowClick}/>
                </>
            }

            <AnomalieDetailDrawer
                anomalie={selectedAnomalie}
                open={drawerOpen}
                onClose={closeDrawer}
                onPatrimoineMaj={handlePatrimoineMaj}
                onCorriger={handleCorriger}
                onIgnorer={handleIgnorer}
                onAcquitter={handleAcquitter}
                onModifierProbleme={handleModifierProbleme}
                problems={problems}
                resolving={resolving}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    anomalies: state.anomalies.anomalies,
    problems: state.problems.problems,
    loading: state.anomalies.loading,
    resolving: state.anomalies.resolving,
    error: state.anomalies.error,
});

export default connect(mapStateToProps)(ControleQualitePage);
